/**
 * The audio thread's half of the microphone: an AudioWorklet processor that
 * hands the captured samples to the page, in batches.
 *
 * It does nothing else. The browser has already mixed the input to one channel
 * (the node is made with `channelCount: 1`, `explicit`), so the processor copies
 * channel 0 of each 128-sample render quantum into a batch and posts the batch
 * when it is full, transferring its buffer rather than copying it again.
 * Resampling and framing happen on the page (`pcm-stream.ts`), where they can be
 * tested; the audio thread only has to never fall behind.
 *
 * A worklet module is loaded by URL, and one written as its own file would be a
 * second build entry. It is small enough to be a string here, and a Blob URL
 * made once per page loads it into every context that asks.
 */

/** The processor's registered name. */
export const CAPTURE_PROCESSOR = 'alluzo-capture';

/** Samples a batch holds: 1024 is eight render quanta, about 21 ms at 48 kHz. */
export const CAPTURE_BATCH = 1024;

const SOURCE = `
class AlluzoCapture extends AudioWorkletProcessor {
  constructor(options) {
    super();
    const asked = options && options.processorOptions && options.processorOptions.batch;
    this.size = typeof asked === 'number' && asked > 0 ? asked : ${CAPTURE_BATCH};
    this.batch = new Float32Array(this.size);
    this.at = 0;
  }
  process(inputs) {
    const input = inputs[0];
    const channel = input && input[0];
    if (!channel) return true;
    let read = 0;
    while (read < channel.length) {
      const take = Math.min(this.size - this.at, channel.length - read);
      this.batch.set(channel.subarray(read, read + take), this.at);
      this.at += take;
      read += take;
      if (this.at === this.size) {
        const full = this.batch;
        this.port.postMessage(full, [full.buffer]);
        this.batch = new Float32Array(this.size);
        this.at = 0;
      }
    }
    return true;
  }
}
registerProcessor('${CAPTURE_PROCESSOR}', AlluzoCapture);
`;

let url: string | null = null;

/** The module's URL, made once for the page. */
export const captureWorkletUrl = (): string => {
  if (url === null) url = URL.createObjectURL(new Blob([SOURCE], { type: 'text/javascript' }));
  return url;
};
